import { useEffect, useState } from 'react';

/**
 * Building view has two distinct layouts (Figma): the mobile (S, 0–1023px)
 * full-screen bottom-sheet, and the desktop (M/XL, ≥1024px) page with the app
 * header + centred content column. This hook reports which to render and stays
 * in sync on resize.
 */
const DESKTOP_MIN_WIDTH = 1024;

export const useIsDesktop = (minWidth: number = DESKTOP_MIN_WIDTH): boolean => {
  const query = `(min-width: ${minWidth}px)`;
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    setIsDesktop(mql.matches);
    // Safari <14 uses addListener/removeListener.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return isDesktop;
};

export default useIsDesktop;
